import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, Zap, Shield, AlertCircle, ChevronDown, ChevronUp, Play, Download, ExternalLink, Wrench } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';
import { useScrollHighlight } from '../hooks/useScrollHighlight';
import { adminGetProductionQueue, adminGenerateProductionPlan, adminGetProductionPlans, downloadCutSheetPdf } from '../../services/api';

// ── Tooltip wrapper — shows after 800ms hover ─────────────────────────────────
function Tooltip({ text, children }) {
  return (
    <div className="relative group">
      {children}
      <div
        className="absolute bottom-full left-1/2 z-30 mb-2 w-56 bg-[#111111] border border-white/20 px-3 py-2.5 text-xs text-zinc-300 leading-relaxed pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150"
        style={{ transitionDelay: '0.7s', transform: 'translateX(-50%)' }}
      >
        {text}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#111111]" />
      </div>
    </div>
  );
}

// ── Pipeline status card — clickable + hover tooltip ─────────────────────────
function StatusCard({ count, label, tooltip, color, borderColor, bgColor, statusKey }) {
  // Not clickable — just informational with hover tooltip
  return (
    <Tooltip text={tooltip}>
      <div data-testid={`stat-card-${statusKey || label}`}
        className={`border p-4 text-center ${borderColor} ${bgColor} bg-[#1a1a1a]`}>
        <div className={`font-bebas text-3xl tracking-wider ${color}`}>{count ?? 0}</div>
        <div className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5 leading-tight">{label}</div>
      </div>
    </Tooltip>
  );
}

// ── Production Queue table ────────────────────────────────────────────────────
function QueueSection({ queue, canGenerate, allOnFloor, highlightId }) {
  const stages = [
    {
      status: 'ready_for_production',
      label: 'WAITING FOR CUT SHEET',
      sublabel: 'Validated & paid. Click "Plan Today\'s Cut Sheet" above to include these.',
      borderCls: 'border-green-500/30', textCls: 'text-green-400',
    },
    {
      status: 'batched',
      label: 'CUT SHEET QUEUE',
      sublabel: 'Auto-added when cut sheet was generated. Click "Start Production" when ready.',
      borderCls: 'border-cyan-500/30', textCls: 'text-cyan-400',
    },
    {
      status: 'in_production',
      label: 'ON THE FLOOR',
      sublabel: 'Machinist is working on it. Go to Serialization when machining is complete.',
      borderCls: 'border-blue-500/30', textCls: 'text-blue-400',
    },
  ];

  return (
    <div>
      {stages.map(stage => {
        const stageOrders = (queue?.orders || []).filter(o => o.status === stage.status);
        if (!stageOrders.length) return null;
        return (
          <div key={stage.status} className="mb-5">
            <div className={`border-l-4 ${stage.borderCls} pl-4 py-2 mb-3 flex items-center justify-between`}>
              <div>
                <span className={`font-bebas text-lg tracking-wider ${stage.textCls}`}>{stage.label}</span>
                <span className="font-inter text-xs text-zinc-500 ml-3 block sm:inline">{stage.sublabel}</span>
              </div>
              <span className="font-bebas text-sm text-zinc-500 flex-shrink-0 ml-3">
                {stageOrders.length} order{stageOrders.length !== 1 ? 's' : ''} · {stageOrders.reduce((s, o) => s + (o.total_qty || 0), 0)} units
              </span>
            </div>
            <div className="bg-[#1a1a1a] border border-white/10 overflow-x-auto">
              <table className="w-full" data-testid={`queue-table-${stage.status}`}>
                <thead>
                  <tr className="border-b border-white/10">
                    {['Order', 'Type', 'Business / Customer', 'Lines', 'Units', 'Serials', 'NFA'].map(h => (
                      <th key={h} className="px-3 py-2 text-left font-inter text-[10px] text-zinc-500 uppercase tracking-widest">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {stageOrders.map(o => (
                    <tr
                      key={o._id}
                      id={`hl-${o._id}`}
                      className={`hover:bg-white/2 transition-colors ${
                        highlightId === o._id ? 'bg-orange-500/10 outline outline-2 outline-orange-500/60' : ''
                      }`}
                    >
                      <td className="px-3 py-2">
                        <Link to={`/admin/orders/${o._id}`} className="font-inter text-sm font-semibold text-white hover:text-orange-400 transition-colors">
                          {o.order_number}
                        </Link>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`font-inter text-[10px] font-bold px-2 py-0.5 border uppercase tracking-wider ${o.type === 'dealer' ? 'text-orange-400 bg-orange-500/10 border-orange-500/20' : 'text-blue-400 bg-blue-500/10 border-blue-500/20'}`}>{o.type || 'public'}</span>
                      </td>
                      <td className="px-3 py-2 font-inter text-sm text-zinc-300">{o.business_name || o.customer_info?.name || '—'}</td>
                      <td className="px-3 py-2 text-center font-inter text-sm text-zinc-400">{o.items?.length || 0}</td>
                      <td className="px-3 py-2 text-center font-bebas text-base text-white">{o.total_qty || 0}</td>
                      <td className="px-3 py-2 text-center font-inter text-xs">
                        <span className={o.serialization_complete ? 'text-green-400 font-semibold' : 'text-zinc-500'}>
                          {o.serial_count || 0}/{o.total_qty || 0}
                        </span>
                      </td>
                      <td className="px-3 py-2">{o.has_nfa_items && <Shield size={13} className="text-orange-500" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Stage action */}
            <div className="mt-2 flex items-center gap-3">
              {stage.status === 'batched' && (
                <span className="font-inter text-xs text-zinc-500">
                  Use <strong className="text-orange-400">"FINALIZE SHOP FLOOR PDF"</strong> on the cut sheet above to move these to the floor and start production tracking.
                </span>
              )}
              {stage.status === 'in_production' && (
                <Link to="/admin/serialization" className="flex items-center gap-1.5 font-bebas text-sm tracking-widest text-blue-400 hover:text-white border border-blue-500/30 px-3 py-1.5 transition-colors">
                  → GO TO SERIALIZATION
                </Link>
              )}
            </div>
          </div>
        );
      })}

      {allOnFloor && (
        <div className="text-center py-8 bg-blue-500/5 border border-blue-500/20">
          <p className="font-bebas text-xl text-blue-400 tracking-wider mb-1">ALL ORDERS ARE ON THE FLOOR</p>
          <p className="font-inter text-sm text-zinc-500">
            The cut sheet button activates when new validated orders arrive or you add items to the{' '}
            <Link to="/admin/inventory" className="text-orange-400 hover:underline">Inventory Build Queue</Link>.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AdminProduction() {
  const { token } = useAdminAuth();
  const navigate = useNavigate();
  const highlightId = useScrollHighlight();
  const [queue, setQueue] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [activeRuns, setActiveRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState('');
  const [pdfDownloading, setPdfDownloading] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);

  const BASE = process.env.REACT_APP_BACKEND_URL;

  const load = () => {
    setLoading(true);
    Promise.all([
      adminGetProductionQueue(token),
      adminGetProductionPlans(token),
      fetch(`${BASE}/api/admin/production/runs?status=in_production`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => []),
    ])
      .then(([q, p, runs]) => {
        setQueue(q);
        setPlans(p);
        if (p.length) setCurrentPlan(p[0]);
        setActiveRuns(Array.isArray(runs) ? runs : []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [token]);

  const canGenerate = (queue?.count_by_status?.ready_for_production || 0) + (queue?.count_by_status?.batched || 0) > 0;
  const allOnFloor = (queue?.count || 0) > 0 && !canGenerate;

  // Generate cut sheet then immediately go to planner
  const handlePlanTodaysCutSheet = async () => {
    setGenerating(true);
    setError('');
    try {
      const plan = await adminGenerateProductionPlan(token);
      setCurrentPlan(plan);
      setPlans(prev => [plan, ...prev.filter(p => p.plan_id !== plan.plan_id)]);
      const q = await adminGetProductionQueue(token);
      setQueue(q);
      // Auto-navigate to planner immediately
      navigate(`/admin/production/plan/${plan.plan_id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleStartProduction = async () => {
    if (!window.confirm(`Move all queued orders to "On the Floor"?`)) return;
    setStartingProduction(true);
    try {
      await fetch(`${BASE}/api/admin/production/bulk-start`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      setProductionStarted(true);
      load();
    } catch (err) { setError(err.message); }
    setStartingProduction(false);
  };

  // FINALIZE = PDF (optional, non-blocking) + move orders to production + create run records + navigate to Shop Floor
  const handleFinalize = async (planId) => {
    setFinalizing(true);
    setError('');

    // Step 1: Try to download PDF — non-blocking, failure doesn't stop the workflow
    try {
      const res = await fetch(`${BASE}/api/admin/production/plans/${planId}/pdf?mode=plain`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `WM_CutSheet_${planId}.pdf`;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a); URL.revokeObjectURL(url);
      }
    } catch (pdfErr) {
      console.warn('PDF download failed — continuing with finalization:', pdfErr);
      // PDF failure does NOT stop the workflow
    }

    // Step 2: Finalize the plan (moves orders to floor, creates run records)
    try {
      const result = await fetch(`${BASE}/api/admin/production/plans/${planId}/finalize`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await result.json();
      if (!result.ok) {
        setError(`Finalize failed: ${data.detail || 'Unknown error'}`);
        setFinalizing(false);
        return;
      }
      // Step 3: Always navigate to Shop Floor after successful finalization
      navigate('/admin/shop-floor');
    } catch (err) {
      setError(`Finalize failed: ${err.message}`);
      setFinalizing(false);
    }
  };

  const handleCompleteRun = async (run) => {
    setCompletingRun(run);
    setCompleteQty(run.quantity_planned);
    setCompleteNotes('');
  };

  const submitCompleteRun = async () => {
    try {
      const res = await fetch(`${BASE}/api/admin/production/runs/${completingRun.run_id}/complete`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity_completed: completeQty, notes: completeNotes }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Error'); }
      setCompletingRun(null);
      load();
    } catch (err) { alert(err.message); }
  };

  return (
    <div data-testid="admin-production-page" className="space-y-6">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-bebas text-4xl text-white tracking-wider">CUT SHEET ENGINE</h1>
          <p className="font-inter text-sm text-zinc-500 mt-1">Plan and generate daily cut sheets for the shop floor</p>
        </div>
        {/* Top button: only show when no plan exists yet — once plan is created, Review & Plan takes over */}
        {!currentPlan && (
          <Tooltip text={canGenerate ? `Generate today's cut sheet from queued orders and open the planner to review capacity, materials, and timing.` : `No orders waiting. Add items to the Inventory Build Queue or wait for new validated orders.`}>
            <button
              data-testid="generate-plan-button"
              onClick={handlePlanTodaysCutSheet}
              disabled={generating || !canGenerate}
              className={`flex items-center gap-2 font-bebas text-lg tracking-widest px-6 py-3 transition-colors ${canGenerate ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'}`}
            >
              <Zap size={18} className={generating ? 'animate-pulse' : ''} />
              {generating ? 'GENERATING...' : 'PLAN TODAY\'S CUT SHEET'}
            </button>
          </Tooltip>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3">
          <AlertCircle size={14} />{error}
          <button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-400">✕</button>
        </div>
      )}

      {/* ── Pipeline Status Cards ────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <StatusCard count={queue?.count_by_status?.ready_for_production}
          label="Waiting for Cut Sheet" statusKey="ready_for_production" color="text-green-400"
          borderColor="border-green-500/30" bgColor="bg-green-500/5"
          tooltip="Orders that are validated and paid, ready to be included in a production cut sheet. Click to see them in the Orders pipeline." />
        <StatusCard count={queue?.count_by_status?.batched}
          label="Cut Sheet Queue" statusKey="batched" color="text-cyan-400"
          borderColor="border-cyan-500/30" bgColor="bg-cyan-500/5"
          tooltip="Orders auto-added to the cut sheet when it was generated. Ready to start production — click 'Start Production' below to move them to the floor." />
        <StatusCard count={queue?.count_by_status?.in_production}
          label="On the Floor" statusKey="in_production" color="text-blue-400"
          borderColor="border-blue-500/30" bgColor="bg-blue-500/5"
          tooltip="Orders currently being machined. When the machinist finishes, go to Serialization to assign serial numbers." />
        <StatusCard count={queue?.total_units}
          label="Total Units to Build" statusKey={null} color="text-orange-400"
          borderColor="border-orange-500/20" bgColor="" clickable={false}
          tooltip="Sum of all unit quantities across all three production stages (Waiting + Cut Sheet Queue + On the Floor)." />
        <StatusCard count={queue?.has_nfa ? 'YES' : 'NO'}
          label="Has NFA Items" statusKey={null} color={queue?.has_nfa ? 'text-orange-400' : 'text-zinc-600'}
          borderColor="border-white/10" bgColor="" clickable={false}
          tooltip="Whether any active production orders contain NFA-regulated suppressors. NFA items require serial marking and ATF compliance documentation." />
      </div>

      {/* ── Production Order Queue (collapsible individual order list) ────── */}
      {queue && queue.count > 0 && (
        <QueueSection queue={queue} canGenerate={canGenerate} allOnFloor={allOnFloor} highlightId={highlightId} />
      )}

      {/* ── Current Plan — REVIEW & PLAN is the primary action ────────── */}
      {currentPlan ? (
        <div className="bg-[#111111] border border-white/10">
          {/* STALE PLAN WARNING — shown when new orders arrived after plan was generated */}
          {canGenerate && (
            <div className="flex items-center gap-3 bg-yellow-500/10 border-b border-yellow-500/30 px-6 py-3">
              <AlertCircle size={14} className="text-yellow-400 flex-shrink-0" />
              <p className="font-inter text-sm text-yellow-300 flex-1">
                <strong>{(queue?.count_by_status?.ready_for_production || 0)} order{(queue?.count_by_status?.ready_for_production || 0) !== 1 ? 's' : ''} arrived after this plan was generated</strong> — they're not in the current cut sheet.
                Click <strong>"Review & Plan"</strong> to auto-include them in a fresh plan.
              </p>
            </div>
          )}

          {/* Plan summary row */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-3">
                <span className="font-bebas text-lg text-white tracking-wider">CURRENT CUT SHEET</span>
                <span className="font-inter text-sm text-zinc-500">{currentPlan.plan_id}</span>
                {currentPlan.has_nfa_items && (
                  <span className="font-inter text-[10px] font-bold text-orange-400 bg-orange-500/10 border border-orange-500/20 px-2 py-0.5">CONTAINS NFA</span>
                )}
              </div>
              <p className="font-inter text-xs text-zinc-500 mt-0.5">
                {currentPlan.order_count} orders · {currentPlan.total_runs} product runs · {currentPlan.total_items} units
                &nbsp;·&nbsp; {new Date(currentPlan.generated_at).toLocaleString()}
              </p>
            </div>
          </div>

          {/* THE BIG PRIMARY ACTION — regenerates first if new orders are waiting */}
          <div className="px-6 py-5">
            <button
              data-testid="review-and-plan-link"
              disabled={generating}
              onClick={async () => {
                if (canGenerate) {
                  // New orders are waiting — regenerate plan to include them, then open planner
                  setGenerating(true);
                  setError('');
                  try {
                    const plan = await adminGenerateProductionPlan(token);
                    setCurrentPlan(plan);
                    setPlans(prev => [plan, ...prev]);
                    const q = await adminGetProductionQueue(token);
                    setQueue(q);
                    navigate(`/admin/production/plan/${plan.plan_id}`);
                  } catch (err) { setError(err.message); }
                  setGenerating(false);
                } else {
                  // Plan is current — just open the planner
                  navigate(`/admin/production/plan/${currentPlan.plan_id}`);
                }
              }}
              className="flex items-center justify-center gap-3 w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-2xl tracking-widest py-5 transition-colors"
            >
              <Wrench size={22} />
              {generating ? 'UPDATING PLAN...' : canGenerate ? 'REVIEW & PLAN — INCLUDE NEW ORDERS' : 'REVIEW & PLAN THIS CUT SHEET'}
            </button>
            <p className="font-inter text-xs text-zinc-600 text-center mt-2">
              {canGenerate
                ? `Will add ${queue?.count_by_status?.ready_for_production || 0} new order(s) to the plan before opening the planner`
                : 'Adjust quantities · check shop capacity · review material pull list · add inventory items'}
            </p>
          </div>

          {/* Category summary */}
          {currentPlan.by_category && (
            <div className="px-6 pb-4">
              <div className="flex flex-wrap gap-2">
                {currentPlan.by_category.map(cat => (
                  <div key={cat.category} className={`flex items-center gap-2 border px-3 py-1.5 ${cat.has_nfa ? 'border-orange-500/30 bg-orange-500/5' : 'border-white/10 bg-white/3'}`}>
                    {cat.has_nfa && <Shield size={11} className="text-orange-400" />}
                    <span className="font-bebas text-sm text-white tracking-wide">{(cat.display_name || cat.category).toUpperCase()}</span>
                    <span className="font-inter text-xs text-zinc-500">×{cat.total_quantity}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Generate PDF — FINALIZE = PDF + Start Production + Create Runs */}
          <div className="px-6 py-4 bg-[#0f0f0f] border-t border-white/10 flex items-center justify-between">
            <div>
              <p className="font-inter text-sm font-medium text-white">Ready to hand off to the floor?</p>
              <p className="font-inter text-xs text-zinc-500">
                This downloads the shop floor PDF, moves all queued orders to production, and creates trackable run records for every item on the cut sheet.
              </p>
            </div>
            <button
              data-testid="finalize-shop-floor-pdf"
              onClick={() => handleFinalize(currentPlan.plan_id)}
              disabled={finalizing || !!pdfDownloading}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bebas text-sm tracking-widest px-5 py-2.5 transition-colors whitespace-nowrap ml-4"
            >
              <Download size={14} />{finalizing ? 'FINALIZING...' : 'FINALIZE SHOP FLOOR PDF'}
            </button>
          </div>
        </div>
      ) : (
        /* No plan yet — show prompt */
        canGenerate ? (
          <div className="bg-[#111111] border border-orange-500/30 p-8 text-center">
            <Zap size={36} className="text-orange-500 mx-auto mb-3" />
            <p className="font-bebas text-2xl text-white tracking-wider mb-2">ORDERS ARE WAITING</p>
            <p className="font-inter text-sm text-zinc-400 mb-5">
              {(queue?.count_by_status?.ready_for_production || 0) + (queue?.count_by_status?.batched || 0)} orders are ready. Generate today's cut sheet to start planning.
            </p>
            <button onClick={handlePlanTodaysCutSheet} disabled={generating}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bebas text-xl tracking-widest px-8 py-4 transition-colors mx-auto">
              <Zap size={20} className={generating ? 'animate-pulse' : ''} />
              {generating ? 'GENERATING...' : 'PLAN TODAY\'S CUT SHEET →'}
            </button>
          </div>
        ) : (
          <div className="bg-[#111111] border border-white/10 p-6 text-center">
            <p className="font-bebas text-lg text-zinc-600 tracking-wider">NO CUT SHEET YET</p>
            <p className="font-inter text-sm text-zinc-600 mt-1">Validated orders appear here when ready for production.</p>
          </div>
        )
      )}

      {/* ── Shop Floor link — when runs are active, point to the right place ── */}
      {/* ── Shop Floor status link (when runs are active) ──────────────── */}
      {activeRuns.length > 0 && (
        <div className="flex items-center gap-3 bg-blue-500/10 border border-blue-500/20 px-5 py-4">
          <div className="flex-1">
            <p className="font-inter text-sm text-white font-medium">
              {activeRuns.length} production run{activeRuns.length !== 1 ? 's' : ''} are active on the floor
            </p>
            <p className="font-inter text-xs text-zinc-500 mt-0.5">
              The machinist's work is tracked on the Shop Floor page.
            </p>
          </div>
          <Link to="/admin/shop-floor"
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bebas text-sm tracking-widest px-4 py-2.5 transition-colors whitespace-nowrap">
            → VIEW SHOP FLOOR
          </Link>
        </div>
      )}

      {/* ── Plan History (collapsible) ───────────────────────────────────── */}
      {plans.length > 1 && (
        <div className="border border-white/10">
          <button
            onClick={() => setHistoryOpen(!historyOpen)}
            className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/3 transition-colors"
          >
            <span className="font-bebas text-lg text-zinc-400 tracking-wider">PLAN HISTORY ({plans.length - 1} previous)</span>
            {historyOpen ? <ChevronUp size={16} className="text-zinc-500" /> : <ChevronDown size={16} className="text-zinc-500" />}
          </button>
          {historyOpen && (
            <div className="border-t border-white/10 divide-y divide-white/5">
              {plans.slice(1, 10).map(p => (
                <div key={p.plan_id} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <span className="font-inter text-sm text-zinc-400">{p.plan_id}</span>
                    <span className="font-inter text-xs text-zinc-600 ml-3">
                      {new Date(p.generated_at).toLocaleDateString()} · {p.order_count} orders · {p.total_items} units
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/admin/production/plan/${p.plan_id}`} className="flex items-center gap-1.5 font-inter text-xs border border-white/10 hover:border-orange-500/40 text-zinc-400 hover:text-orange-400 px-3 py-1.5 transition-colors">
                      <ExternalLink size={11} />Plan
                    </Link>
                    <button onClick={async () => { setPdfDownloading(p.plan_id); try { await downloadCutSheetPdf(token, p.plan_id, 'plain'); } catch(e){alert(e.message);} setPdfDownloading(''); }} disabled={!!pdfDownloading} className="flex items-center gap-1.5 font-inter text-xs border border-white/10 hover:border-white/30 text-zinc-400 hover:text-white px-3 py-1.5 transition-colors disabled:opacity-40">
                      <Download size={11} />PDF
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
